import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {
  devtools,
  persist,
  createJSONStorage,
  type StateStorage,
} from 'zustand/middleware';
import {
  DocumentChoice,
  IMAGE_SERVICE_ORIGIN,
  ImageDetails,
  IVGuestData,
  IV_DOCUMENT_IMAGE_TYPES,
  LivenessAction,
  getSafeStorage,
  isStorageAvailable,
} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics} from '../analytics';
import {safeCaptureException} from '../utils/sentry';

export const DEFAULT_IMAGE_DETAILS = {
  [IV_DOCUMENT_IMAGE_TYPES.front]: {
    id: '',
    image: null,
    uploaded: false,
    mrzDetected: false,
    faceDetected: false,
    type: IV_DOCUMENT_IMAGE_TYPES.front,
  },
  [IV_DOCUMENT_IMAGE_TYPES.back]: {
    id: '',
    image: null,
    uploaded: false,
    mrzDetected: false,
    faceDetected: false,
    service: IMAGE_SERVICE_ORIGIN.ocr,
    type: IV_DOCUMENT_IMAGE_TYPES.back,
  },
  [IV_DOCUMENT_IMAGE_TYPES.selfie]: {
    id: '',
    image: null,
    uploaded: false,
    faceDetected: false,
    service: IMAGE_SERVICE_ORIGIN.biomatch,
    type: IV_DOCUMENT_IMAGE_TYPES.selfie,
  },
};

export type IVStoreState = {
  isRemoteSession: boolean;
  setupData: {
    documentType?: DocumentChoice;
    nationality?: string;
  };
  imagesDetails: {
    [key in IV_DOCUMENT_IMAGE_TYPES]: Partial<ImageDetails> & {
      type: IV_DOCUMENT_IMAGE_TYPES;
    };
  };
  ocrResultData: {
    ocrPassed: boolean;
    guestData?: IVGuestData;
  };
  biomatchResultData: {
    biomatchPassed: boolean;
    distance: number | null;
  };
  livenessResultData?: {
    passed?: boolean;
    completedActions?: LivenessAction[];
    livenessVideoUrl?: string;
    performedBy?: 'CLIENT' | 'SERVER';
  };
  isIvFailed: boolean;
  isIvSkipped: boolean;
  isPropertyQRScanned: boolean;
  declaredIvBypassAge: number | null;
};

export const initialState: IVStoreState = {
  isRemoteSession: false,
  setupData: {},
  imagesDetails: DEFAULT_IMAGE_DETAILS,
  ocrResultData: {
    ocrPassed: false,
  },
  biomatchResultData: {
    biomatchPassed: false,
    distance: 0,
  },
  livenessResultData: undefined,
  isIvFailed: false,
  isIvSkipped: false,
  isPropertyQRScanned: false,
  declaredIvBypassAge: null,
};

const resetListeners = new Set<() => void>();

export function onIVStateReset(listener: () => void) {
  resetListeners.add(listener);
  return () => {
    resetListeners.delete(listener);
  };
}

const notifyReset = () => resetListeners.forEach(listener => listener());

const omitRawImages = (imagesDetails: IVStoreState['imagesDetails']) =>
  Object.entries(imagesDetails).reduce((acc, [type, details]) => {
    acc[type as IV_DOCUMENT_IMAGE_TYPES] = {...details, image: null};
    return acc;
  }, {} as IVStoreState['imagesDetails']);

const ivStorage = () => getSafeStorage('sessionStorage');

const ivPersistStorage: StateStorage = {
  getItem: name => ivStorage().getItem(name),
  setItem: (name, value) => {
    try {
      sessionStorage.setItem(name, value);
    } catch (error) {
      ivStorage().setItem(name, value);

      // Browsers that block storage entirely (blocked cookies, private mode in an
      // iframe) throw on every write, so only the recoverable quota failures are worth
      // reporting.
      if (!isStorageAvailable('sessionStorage')) return;

      console.error('Failed to persist IV store to sessionStorage', error);
      // Through the shared helper, so the report reaches the SDK's own isolated
      // scope too — the SDK never claims the global Sentry client.
      safeCaptureException(error, `IV store persist (${value.length} chars)`, {
        area: 'identity-verification',
        storage: 'sessionStorage',
        storage_key: name,
      });
    }
  },
  removeItem: name => ivStorage().removeItem(name),
};

type Actions = {
  setDocumentType: (documentType: DocumentChoice) => void;
  setNationality: (nationality: string) => void;
  setOCRDocumentPassed: (ocrPassed: boolean) => void;
  setOCRGuestData: (guestData?: IVGuestData) => void;
  setBiomatchPassed: (biomatchPassed: boolean) => void;
  setDistanceBiomatchComparing: (distance: number) => void;
  setImageDetails: (
    payload: Partial<ImageDetails> & {type: IV_DOCUMENT_IMAGE_TYPES},
  ) => void;
  resetIVConfigState: () => void;
  resetIVSetupFormDate: () => void;
  resetAllIVState: () => void;
  initIVState: () => void;
  sync: (identityVerificationSchema: Partial<IVStoreState>) => void;
  setRemoteSession: (flag: boolean) => void;
  setLivenessResultData: (
    livenessResultData: Partial<IVStoreState['livenessResultData']>,
  ) => void;
  setIsIvFailed: (isIvFailed: boolean) => void;
  skipIV: () => void;
  setPropertyQRScanned: (isPropertyQRScanned: boolean) => void;
  setDeclaredIvBypassAge: (declaredIvBypassAge: number | null) => void;
};

export const useIVStore = create<IVStoreState & Actions>()(
  devtools(
    persist(
      immer(set => ({
        ...initialState,
        setDocumentType: documentType => {
          set(state => {
            state.setupData.documentType = documentType;
          });
          Analytics.addEventProperties({
            document_type: documentType?.value,
            document_label: documentType?.label,
            has_two_sides: documentType?.two_sides,
          });
          Analytics.track(ANALYTIC_EVENTS.ivDocumentTypeSelected, {
            document_type: documentType?.value,
          });
        },
        setNationality: nationality => {
          set(state => {
            state.setupData.nationality = nationality;
          });
          Analytics.addEventProperties({nationality});
        },
        setOCRDocumentPassed: ocrPassed =>
          set(state => {
            state.ocrResultData.ocrPassed = ocrPassed;
          }),
        setOCRGuestData: guestData =>
          set(state => {
            state.ocrResultData.guestData = guestData;
          }),
        setBiomatchPassed: biomatchPassed =>
          set(state => {
            state.biomatchResultData.biomatchPassed = biomatchPassed;
          }),
        setDistanceBiomatchComparing: distance =>
          set(state => {
            state.biomatchResultData.distance = distance;
          }),
        setImageDetails: ({type, ...imageDetails}) =>
          set(state => {
            state.imagesDetails[type] = {
              ...state.imagesDetails[type],
              ...imageDetails,
              uploaded: Boolean(imageDetails.image),
            };
          }),
        resetIVConfigState: () =>
          set(state => {
            state.biomatchResultData = initialState.biomatchResultData;
            state.imagesDetails = initialState.imagesDetails;
            state.ocrResultData = initialState.ocrResultData;
          }),
        resetIVSetupFormDate: () =>
          set(state => {
            state.setupData = initialState.setupData;
          }),
        resetAllIVState: () => {
          set(() => initialState, false);
          notifyReset();
        },
        initIVState: () =>
          set(
            state => ({
              ...initialState,
              isPropertyQRScanned: state.isPropertyQRScanned,
            }),
            false,
          ),
        sync: identityVerificationSchema => set(() => identityVerificationSchema),
        setRemoteSession: flag => {
          set(state => {
            state.isRemoteSession = flag;
          });
          Analytics.addEventProperties({is_remote_session: flag});
        },
        setLivenessResultData: livenessResultData => {
          set(state => {
            state.livenessResultData = {
              ...state.livenessResultData,
              ...livenessResultData,
            };
          });
          if (livenessResultData?.performedBy) {
            Analytics.addEventProperties({
              liveness_performed_by: livenessResultData.performedBy,
            });
          }
          if (livenessResultData?.passed === true) {
            Analytics.track(ANALYTIC_EVENTS.ivLivenessPassed, {
              liveness_performed_by: livenessResultData.performedBy,
            });
          }
        },
        setIsIvFailed: isIvFailed =>
          set(state => {
            state.isIvFailed = isIvFailed;
          }),
        skipIV: () => {
          set(() => ({...initialState, isIvSkipped: true}), false);
          notifyReset();
        },
        setPropertyQRScanned: isPropertyQRScanned =>
          set(state => {
            state.isPropertyQRScanned = isPropertyQRScanned;
          }),
        setDeclaredIvBypassAge: declaredIvBypassAge =>
          set(state => {
            state.declaredIvBypassAge = declaredIvBypassAge;
          }),
      })),
      {
        name: 'iv-state',
        storage: createJSONStorage(() => ivPersistStorage),
        partialize: state => ({
          ...state,
          imagesDetails: omitRawImages(state.imagesDetails),
          ocrResultData: {ocrPassed: state.ocrResultData.ocrPassed},
        }),
      },
    ),
  ),
);
